import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';

import { informacionEvento, limpiarEvento } from '../../actions/eventos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const tiposEmpleado = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' },
    { value: 3, label: 'Freidor' },
    { value: 4, label: 'Vendedor ambulante' },
    { value: 5, label: 'Administrador' }
];

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const formatFechaCorta = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

const DetallesEvento = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const informacion = useSelector(state => state.eventos.informacion);

    const [asistenciaDetalle, setAsistenciaDetalle] = useState(null);

    useEffect(() => {
        if (id) {
            dispatch(informacionEvento(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Eventos', path: '/eventos', activo: false },
                { nombre: 'Detalles del evento', path: `/eventos/detalles/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarEvento());
        };
    }, [id]);

    const ind = informacion?.indicadores;

    const kpisRow1 = [
        { label: 'Ventas',         sublabel: 'Número',  value: ind?.ventas?.numero  ?? '-', icon: 'fa-shopping-cart', color: 'success'   },
        { label: 'Ventas',         sublabel: 'Total',   value: ind?.ventas?.total   != null ? formatNumber(ind.ventas.total, 2, '$', '') : '-', icon: 'fa-dollar-sign', color: 'success' },
        { label: 'Ventas',         sublabel: 'Conos (vendidos / comisionados)', value: (ind?.ventas?.conos ?? '-') + ' / ' + (ind?.ventas?.conos_comisionados ?? '-'), icon: 'fa-ice-cream', color: 'success' },
        { label: 'V. ambulantes',  sublabel: 'Número',  value: ind?.vueltas?.numero ?? '-', icon: 'fa-redo',    color: 'warning'   },
        { label: 'V. ambulantes',  sublabel: 'Total',   value: ind?.vueltas?.total  != null ? formatNumber(ind.vueltas.total, 2, '$', '') : '-', icon: 'fa-dollar-sign', color: 'warning' },
        { label: 'V. ambulantes',  sublabel: 'Conos (vendidos / comisionados)',   value: (ind?.vueltas?.conos ?? '-') + ' / ' + (ind?.vueltas?.conos ?? '-')  ?? '-', icon: 'fa-ice-cream',     color: 'warning'   },
    ];

    const conosVendidos = (ind?.ventas?.conos ?? 0) + (ind?.vueltas?.conos ?? 0);
    const conosComisionados = (ind?.ventas?.conos_comisionados ?? 0) + (ind?.vueltas?.conos ?? 0);

    const kpisRow2 = [
        { label: 'Gastos',   sublabel: 'Número', value: ind?.gastos?.numero  ?? '-',  icon: 'fa-file-invoice-dollar', color: 'danger'    },
        { label: 'Gastos',   sublabel: 'Total',  value: ind?.gastos?.total   != null ? formatNumber(ind.gastos.total, 2, '$', '') : '-',   icon: 'fa-dollar-sign',         color: 'danger'    },
        { label: 'Conos',    sublabel: 'Vendidos / Comisionados', value: (conosVendidos ?? '-') + ' / ' + (conosComisionados ?? '-'),  icon: 'fa-ice-cream', color: 'info' },
        { label: 'Nóminas',  sublabel: 'Número', value: ind?.nominas?.numero ?? '-',  icon: 'fa-users',               color: 'secondary' },
        { label: 'Nóminas',  sublabel: 'Total',  value: ind?.nominas?.total  != null ? formatNumber(ind.nominas.total, 2, '$', '') : '-',  icon: 'fa-dollar-sign',         color: 'secondary' },
        { label: 'Total',    sublabel: 'Ventas + V.Amb − Gastos − Nóminas', value: ind?.total != null ? formatNumber(ind.total, 2, '$', '') : '-', icon: 'fa-balance-scale', color: 'success' },
    ];

    const columnsVentas = [
        { name: 'Folio',    sortable: true, wrap: true, selector: row => row.folio || '-' },
        { name: 'Fecha',    sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Sucursal', sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Método',   sortable: true, wrap: true, selector: row => row.metodo?.nombre || '-' },
        { name: 'Vendedor', sortable: true, wrap: true, grow: 1.5, selector: row => formatNombre(row.vendedor) },
        { name: 'Total',    sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        }
    ];

    const columnsVueltas = [
        { name: 'Folio',       sortable: true, wrap: true, selector: row => row.folio || '-' },
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Sucursal',    sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Vendedor',    sortable: true, wrap: true, grow: 1.5, selector: row => formatNombre(row.vendedor) },
        { name: 'Responsable', sortable: true, wrap: true, grow: 1.5, selector: row => formatNombre(row.responsable) },
        { name: 'Total',       sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizada' : 'Pendiente'}
                </span>
            )
        }
    ];

    const columnsGastos = [
        { name: 'Concepto',   sortable: true, wrap: true, grow: 2,   selector: row => row.concepto || '-' },
        { name: 'Fecha',      sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha) },
        { name: 'Proveedor',  sortable: true, wrap: true, grow: 1.5, selector: row => row.proveedor?.nombre || '-' },
        { name: 'Método',     sortable: true, wrap: true, selector: row => row.metodo?.nombre || '-' },
        { name: 'Total',      sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => row.status != null
                ? <span className={`badge ${row.status ? 'bg-success' : 'bg-warning text-dark'}`}>{row.status ? 'Pagado' : 'Pendiente'}</span>
                : <span className="text-muted">-</span>
        },
        {
            name: 'Adeudado a', sortable: true, wrap: true, 
            selector: row => row.adeudado === 0 ? 'Daniel' : row.adeudado === 1 ? 'Socio' : '-'
        },
        {
            name: 'Reembolsado', sortable: true, wrap: true, 
            cell: row => row.remobolsado != null
                ? <span className={`badge ${row.remobolsado === 1 ? 'bg-success' : 'bg-danger'}`}>{row.remobolsado === 1 ? 'Pagado c/ venta' : 'Aún no'}</span>
                : <span className="text-muted">-</span>
        }
    ];

    const columnsNominas = [
        { name: 'Fecha inicio', sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha_inicio) },
        { name: 'Fecha fin',    sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha_fin) },
        { name: 'Sucursal',     sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Responsable',  sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Empleados',    sortable: true, wrap: true, selector: row => row.total_empleados ?? '-' },
        { name: 'Total',        sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizada' : 'En proceso'}
                </span>
            )
        }
    ];

    const columnsAsistencias = [
        { name: 'Fecha',        sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Sucursal',     sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Responsable',  sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Empleados',    sortable: true, wrap: true,            selector: row => row.total_empleados   ?? '-' },
        { name: 'Asistencias',  sortable: true, wrap: true,            selector: row => row.total_asistencias ?? '-' },
        {
            name: '',
            cell: row => (
                <button className="btn btn-sm btn-info" title="Ver detalle" onClick={() => setAsistenciaDetalle(row)}>
                    <i className="fas fa-eye"></i>
                </button>
            )
        }
    ];

    const columnsRevisiones = [
        { name: 'Fecha creación',  sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha_creacion) },
        { name: 'Fecha revisión',  sortable: true, wrap: true, grow: 1.5, selector: row => row.fecha_revision ? formatFecha(row.fecha_revision) : '-' },
        { name: 'Sucursal',        sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Responsable',     sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Confirmador',     sortable: true, wrap: true, grow: 2,   selector: row => row.confirmador ? formatNombre(row.confirmador) : 'Pendiente' },
        { name: 'Puntos',          sortable: true, wrap: true, selector: row => row.total_puntos != null ? `${row.puntos_bien ?? 0} / ${row.total_puntos}` : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Revisada' : 'Pendiente'}
                </span>
            )
        }
    ];

    const columnsConos = [
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha) },
        { name: 'Sucursal',    sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        { name: 'Responsable', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Cantidad',    sortable: true, wrap: true, selector: row => row.cantidad ?? '-' }
    ];

    const paginationOpts = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const secciones = [
        { id: 'deVentas',       titulo: 'Ventas',                    icon: 'fa-shopping-cart',       color: 'success',   columns: columnsVentas,       data: informacion?.ventas      || [], empty: 'No hay ventas registradas para este evento' },
        { id: 'deVueltas',      titulo: 'Ventas ambulantes',          icon: 'fa-redo',                color: 'warning',   columns: columnsVueltas,       data: informacion?.vueltas     || [], empty: 'No hay ventas ambulantes registradas para este evento' },
        { id: 'deGastos',       titulo: 'Gastos',                    icon: 'fa-file-invoice-dollar', color: 'danger',    columns: columnsGastos,        data: informacion?.gastos      || [], empty: 'No hay gastos registrados para este evento' },
        { id: 'deNominas',      titulo: 'Nóminas',                   icon: 'fa-users',               color: 'secondary', columns: columnsNominas,       data: informacion?.nominas     || [], empty: 'No hay nóminas registradas para este evento' },
        { id: 'deAsistencias',  titulo: 'Asistencias',               icon: 'fa-clipboard-check',     color: 'info',      columns: columnsAsistencias,   data: informacion?.asistencias || [], empty: 'No hay asistencias registradas para este evento' },
        { id: 'deRevisiones',   titulo: 'Revisiones',                icon: 'fa-search',              color: 'primary',   columns: columnsRevisiones,    data: informacion?.revisiones  || [], empty: 'No hay revisiones registradas para este evento' },
        { id: 'deConos',        titulo: 'Reabastecimientos de conos', icon: 'fa-ice-cream',          color: 'success',   columns: columnsConos,         data: informacion?.conos       || [], empty: 'No hay reabastecimientos registrados para este evento' },
    ];

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-calendar text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">{informacion?.nombre || 'Detalles del evento'}</h4>
                        <small className="text-muted">Información completa del evento</small>
                    </div>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <span className={`badge fs-6 px-3 py-2 ${informacion?.cobrado === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                            <i className={`fas ${informacion?.cobrado === true ? 'fa-check-circle' : 'fa-clock'} me-1`}></i>
                            {informacion?.cobrado === true ? 'Las ventas se cobran' : 'Las ventas no se cobran'}
                        </span>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/eventos')}>
                            <i className="fas fa-arrow-left me-1"></i> Volver
                        </button>
                    </div>
                </div>
            </div>

            {/* KPIs fila 1: Ventas + V. ambulantes */}
            <div className="col-12 mb-2">
                <div className="row g-3">
                    {kpisRow1.map(({ label, sublabel, value, icon, color }) => (
                        <div className="col-md-2" key={`${label}-${sublabel}`}>
                            <div className="text-center p-3 rounded bg-light">
                                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-2`} style={{ width: 40, height: 40 }}>
                                    <i className={`fas ${icon} text-${color}`}></i>
                                </div>
                                <div className="text-muted small">{label}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sublabel}</div>
                                <div className="fw-bold">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* KPIs fila 2: Gastos + Nóminas + Total */}
            <div className="col-12 mb-4">
                <div className="row g-3">
                    {kpisRow2.map(({ label, sublabel, value, icon, color }) => (
                        <div className="col-md" key={`${label}-${sublabel}`}>
                            <div className="text-center p-3 rounded bg-light">
                                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-2`} style={{ width: 40, height: 40 }}>
                                    <i className={`fas ${icon} text-${color}`}></i>
                                </div>
                                <div className="text-muted small">{label}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sublabel}</div>
                                <div className="fw-bold">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Acordeón */}
            <div className="col-12">
                <div className="accordion" id="accordionDetallesEvento">

                    {/* Sección 1: Información del evento */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="deHeadingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#deCollapseInfo" aria-expanded="true" aria-controls="deCollapseInfo">
                                <i className="fas fa-info-circle text-primary me-2"></i>
                                Información del evento
                            </button>
                        </h2>
                        <div id="deCollapseInfo" className="accordion-collapse collapse show" aria-labelledby="deHeadingInfo" data-bs-parent="#accordionDetallesEvento">
                            <div className="accordion-body">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Nombre</div>
                                            <div className="fw-semibold">{informacion?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Método de cobro</div>
                                            <div className="fw-semibold">{informacion?.metodo?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-success border-3 ps-3">
                                            <div className="text-muted small mb-1">Fecha inicio</div>
                                            <div className="fw-semibold">
                                                {informacion?.fecha_inicio
                                                    ? new Date(informacion.fecha_inicio).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
                                                    : '-'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-danger border-3 ps-3">
                                            <div className="text-muted small mb-1">Fecha fin</div>
                                            <div className="fw-semibold">
                                                {informacion?.fecha_fin
                                                    ? new Date(informacion.fecha_fin).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
                                                    : '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Secciones de listados */}
                    {secciones.map(({ id, titulo, icon, color, columns, data, empty }) => (
                        <div className="accordion-item border-0 shadow-sm mb-2" key={id}>
                            <h2 className="accordion-header" id={`${id}Heading`}>
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#${id}Collapse`} aria-expanded="false" aria-controls={`${id}Collapse`}>
                                    <i className={`fas ${icon} text-${color} me-2`}></i>
                                    {titulo}
                                    {data.length > 0 && (
                                        <span className={`badge bg-${color} rounded-pill ms-2`}>{data.length}</span>
                                    )}
                                </button>
                            </h2>
                            <div id={`${id}Collapse`} className="accordion-collapse collapse" aria-labelledby={`${id}Heading`} data-bs-parent="#accordionDetallesEvento">
                                <div className="accordion-body p-0">
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationPerPage={20}
                                        paginationComponentOptions={paginationOpts}
                                        highlightOnHover
                                        noDataComponent={<div className="py-4 text-center text-muted">{empty}</div>}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            {/* Modal detalle de asistencia */}
            <Modal show={asistenciaDetalle != null} size="lg" backdrop="static" centered onHide={() => setAsistenciaDetalle(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Detalle de asistencia</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {asistenciaDetalle && (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="border-start border-primary border-3 ps-3">
                                        <div className="text-muted small mb-1">Fecha</div>
                                        <div className="fw-semibold">{formatFecha(asistenciaDetalle.fecha)}</div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="border-start border-primary border-3 ps-3">
                                        <div className="text-muted small mb-1">Responsable</div>
                                        <div className="fw-semibold">{formatNombre(asistenciaDetalle.responsable)}</div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="border-start border-secondary border-3 ps-3">
                                        <div className="text-muted small mb-1">Empleados</div>
                                        <div className="fw-semibold">{asistenciaDetalle.total_empleados ?? '-'}</div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="border-start border-success border-3 ps-3">
                                        <div className="text-muted small mb-1">Asistencias</div>
                                        <div className="fw-semibold">{asistenciaDetalle.total_asistencias ?? '-'}</div>
                                    </div>
                                </div>
                            </div>

                            <DataTable
                                columns={[
                                    { name: 'Empleado', sortable: true, grow: 2, selector: row => formatNombre(row.empleado) },
                                    { name: 'Tipo',     sortable: true, selector: row => tiposEmpleado.find(t => t.value === row.empleado?.tipo)?.label || '-' },
                                    {
                                        name: 'Asistencia', sortable: true,
                                        cell: row => (
                                            <span className={`badge ${row.asistio ? 'bg-success' : 'bg-danger'}`}>
                                                {row.asistio ? 'Asistió' : 'No asistió'}
                                            </span>
                                        )
                                    }
                                ]}
                                data={asistenciaDetalle.empleados || []}
                                highlightOnHover
                                noDataComponent="Sin empleados registrados"
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setAsistenciaDetalle(null)}>Cerrar</button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default DetallesEvento;
